import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useChatSupport } from '../contexts/ChatSupportContext';

const INITIAL_DELAY_MS = 12_000;
const VISIBLE_MS = 8_000;
const REPEAT_INTERVAL_MS = 3 * 60_000;
const DISMISS_STORAGE_KEY = 'ninja-teaser-dismissed';

const ChatTeaserBubble: React.FC = () => {
  const { user, token } = useAuth();
  const { isOpen } = useChatSupport();
  const location = useLocation();

  const [message, setMessage] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const hideTimerRef = useRef<number | null>(null);
  const showTimerRef = useRef<number | null>(null);

  const shouldHide =
    !user ||
    user.consumesLicense === false ||
    isOpen ||
    dismissed ||
    location.pathname === '/login' ||
    location.pathname === '/register-admin' ||
    location.pathname === '/pdf-export-preview' ||
    location.pathname.startsWith('/auth');

  // Fetch one teaser message for the current path.
  const fetchMessage = async (): Promise<string | null> => {
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const res = await fetch(
        `${apiUrl}/api/chat/support/teaser?path=${encodeURIComponent(location.pathname)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return null;
      const data = await res.json();
      return typeof data.message === 'string' && data.message.length > 0 ? data.message : null;
    } catch {
      return null;
    }
  };

  // Show one bubble: fetch a message, reveal, hide after VISIBLE_MS.
  const showOnce = async () => {
    if (shouldHide) return;
    const msg = await fetchMessage();
    if (!msg) return;
    setMessage(msg);
    setVisible(true);
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => setVisible(false), VISIBLE_MS);
  };

  useEffect(() => {
    if (shouldHide) {
      setVisible(false);
      if (showTimerRef.current) window.clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      return;
    }

    // Schedule the first appearance, then a repeating cadence.
    showTimerRef.current = window.setTimeout(() => {
      showOnce();
      const interval = window.setInterval(() => {
        showOnce();
      }, REPEAT_INTERVAL_MS);
      // Stash the interval id on the ref so cleanup catches it.
      showTimerRef.current = interval as unknown as number;
    }, INITIAL_DELAY_MS);

    return () => {
      if (showTimerRef.current) {
        window.clearTimeout(showTimerRef.current);
        window.clearInterval(showTimerRef.current);
      }
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
    // location.pathname changes restart the cadence with the new route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHide, location.pathname]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_STORAGE_KEY, '1');
    } catch {
      // Ignore storage errors
    }
  };

  if (shouldHide || !visible || !message) return null;

  return (
    <div
      className="fixed z-40 print:hidden"
      style={{ bottom: '140px', right: '40px', maxWidth: '260px' }}
      role="status"
      aria-live="polite"
    >
      <div
        onClick={handleDismiss}
        className="relative bg-white dark:bg-dark-surface text-secondary dark:text-dark-text px-4 py-3 pr-7 rounded-2xl shadow-xl border border-gray-200 dark:border-dark-border text-sm leading-snug cursor-pointer animate-fade-in"
      >
        {message}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          className="absolute top-1 right-1 p-1 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-3 h-3" />
        </button>
        {/* Speech-bubble tail pointing toward the ninja icon (lower right) */}
        <span
          className="absolute -bottom-2 right-8 w-4 h-4 bg-white dark:bg-dark-surface border-r border-b border-gray-200 dark:border-dark-border"
          style={{ transform: 'rotate(45deg)' }}
        />
      </div>
    </div>
  );
};

export default ChatTeaserBubble;
